// Realtor Plugin Manifest
// CRM система для риелторов

import { lazy } from 'react';
import {
  IconSearch,
  IconBuilding,
  IconGift,
  IconFileText,
  IconMenu2,
  IconCreditCard,
  IconHistory,
} from '@tabler/icons-react';
import type { PluginManifest, DeepLinkParams } from '../types';
import { REALTOR_EVENT_TYPES } from './types/events';
import type { UserMode } from './store';

// Lazy load pages
const ApartmentSearchPage = lazy(() => import('./pages/ApartmentSearch'));
const ApartmentDetailPage = lazy(() => import('./pages/ApartmentDetail'));
const ResidentialListPage = lazy(() => import('./pages/ResidentialList'));
const ResidentialDetailPage = lazy(() => import('./pages/ResidentialDetail'));
const PromotionsListPage = lazy(() => import('./pages/PromotionsList'));
const ReportGeneratorPage = lazy(() => import('./pages/ReportGenerator'));
const ReportsHistoryPage = lazy(() => import('./pages/ReportsHistory'));
const SubscriptionPage = lazy(() => import('./pages/Subscription'));
const AdminDashboardPage = lazy(() => import('./pages/AdminDashboard'));
const DevelopersPage = lazy(() => import('./pages/admin/DevelopersPage'));
const DeveloperDetailsPage = lazy(() => import('./pages/admin/DeveloperDetailsPage'));
const ComplexDetailsPage = lazy(() => import('./pages/admin/ComplexDetailsPage'));
const FeedsPage = lazy(() => import('./pages/admin/FeedsPage'));
const FeedDetailsPage = lazy(() => import('./pages/admin/FeedDetailsPage'));

// Placeholder for agent contact page - simple component without JSX
const AgentContactPage = lazy(() => import('./pages/AgentContact'));

export interface RealtorDeepLinkParams extends DeepLinkParams {
  mode: UserMode;
  agentId?: string;
  refCode?: string;
}

function parseDeepLink(startParam: string): RealtorDeepLinkParams | null {
  if (!startParam.startsWith('realtor_')) return null;
  
  const parts = startParam.split('_');
  const action = parts[1];
  
  if (action === 'agent') {
    return { mode: 'agent' };
  }
  
  if (action === 'client' && parts.length >= 3) {
    return { mode: 'client', agentId: parts.slice(2).join('_') };
  }
  
  if (action === 'ref' && parts.length >= 3) {
    return { mode: 'agent', refCode: parts.slice(2).join('_') };
  }
  
  return null;
}

export const realtorManifest: PluginManifest = {
  id: 'realtor',
  name: 'CRM Риелтор',
  description: 'CRM система для риелторов',
  version: '1.0.0',
  basePath: '/realtor',
  permissionPrefix: 'realtor',
  enabled: true,
  
  routes: [
    { path: '/search', component: ApartmentSearchPage },
    { path: '/apartment/:id', component: ApartmentDetailPage },
    { path: '/complexes', component: ResidentialListPage },
    { path: '/complex/:id', component: ResidentialDetailPage },
    { path: '/promotions', component: PromotionsListPage },
    { path: '/report', component: ReportGeneratorPage },
    { path: '/reports', component: ReportsHistoryPage },
    { path: '/subscription', component: SubscriptionPage },
    { path: '/agent', component: AgentContactPage },
    { path: '/admin', component: AdminDashboardPage, requireAdmin: true },
    { path: '/admin/developers', component: DevelopersPage, requireAdmin: true },
    { path: '/admin/developers/:id', component: DeveloperDetailsPage, requireAdmin: true },
    { path: '/admin/complexes/:id', component: ComplexDetailsPage, requireAdmin: true },
    { path: '/admin/feeds', component: FeedsPage, requireAdmin: true },
    { path: '/admin/feeds/:id', component: FeedDetailsPage, requireAdmin: true },
  ],
  
  navigationItems: [
    { path: '/realtor/search', icon: IconSearch, label: 'Поиск' },
    { path: '/realtor/complexes', icon: IconBuilding, label: 'ЖК' },
    { path: '/realtor/promotions', icon: IconGift, label: 'Акции' },
    { path: '/realtor/reports', icon: IconFileText, label: 'Подборки' },
    { path: '/more', icon: IconMenu2, label: 'Ещё' },
  ],
  
  dashboardWidgets: [
    {
      title: 'Поиск квартир',
      description: 'Подбор квартир по параметрам',
      icon: IconSearch,
      path: '/realtor/search',
      color: 'teal',
    },
    {
      title: 'Жилые комплексы',
      description: 'Каталог ЖК',
      icon: IconBuilding,
      path: '/realtor/complexes',
      color: 'blue',
    },
    {
      title: 'Акции',
      description: 'Специальные предложения',
      icon: IconGift,
      path: '/realtor/promotions',
      color: 'pink',
    },
    {
      title: 'Генератор отчетов',
      description: 'Создание PDF подборок',
      icon: IconFileText,
      path: '/realtor/report',
      color: 'orange',
    },
    {
      title: 'История отчетов',
      description: 'Созданные подборки',
      icon: IconHistory,
      path: '/realtor/reports',
      color: 'grape',
    },
    {
      title: 'Подписка',
      description: 'Тарифы и оплата',
      icon: IconCreditCard,
      path: '/realtor/subscription',
      color: 'green',
    },
    {
      title: 'Админ панель',
      description: 'Управление застройщиками и фидами',
      icon: IconBuilding,
      path: '/realtor/admin',
      color: 'red',
      requireAdmin: true,
    },
  ],
  
  mqttConfig: {
    eventTypes: Object.values(REALTOR_EVENT_TYPES),
    onEvent: (eventType, payload) => {
      console.log(`[Realtor] Event: ${eventType}`, payload);
    },
    subscribeToSystemEvents: true,
  },
  
  deepLinkHandler: parseDeepLink,
  
  stateSlice: {
    name: 'realtor',
    initialState: {
      userMode: 'agent' as UserMode,
      assignedAgentId: null as string | null,
      refCode: null as string | null,
    },
  },
};
